import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ChevronDown, Globe } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';

// Services with English slugs for routing
const serviceSlugMap = [
  "claims-consulting",
  "medical-records-consulting",
  "insured-patients",
  "data-analysis-consulting",
  "admin-consulting",
  "technical-consulting",
  "events-management",
  "cme-and-medical-staff-training",
  "elite-services"
];

const Header = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const { language, toggleLanguage, t, services, isRTL } = useLanguage();

  const handleServicesEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setServicesOpen(true);
  };

  const handleServicesLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setServicesOpen(false), 1500);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-800 text-white py-2 text-sm">
        <div className="container mx-auto px-4">
          <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Phone size={14} />
                {isRTL ? (
                  <span>هاتف: <bdi>{companyInfo.phone}</bdi></span>
                ) : (
                  <span>Phone: {companyInfo.phone}</span>
                )}
              </div>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {isRTL ? (
                  <span>فاكس: <bdi>{companyInfo.fax}</bdi></span>
                ) : (
                  <span>Fax: {companyInfo.fax}</span>
                )}
              </div>
            </div>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Mail size={14} />
              {isRTL ? (
                <span>بريد إلكتروني: <bdi>{companyInfo.email}</bdi></span>
              ) : (
                <span>Email: {companyInfo.email}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between py-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Target For Health Consulting" 
                className="h-24 w-auto"
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.home}
              </Link>
              <Link to="/about" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.about}
              </Link>
              <div 
                className="relative"
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleServicesLeave}
              >
                <button className={`text-slate-700 hover:text-cyan-500 font-medium transition-colors flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {t.nav.services}
                  <ChevronDown size={16} />
                </button>
                {servicesOpen && (
                  <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} pt-2 w-72 z-50`}>
                    <div className="bg-white shadow-lg rounded-md py-2 border border-slate-200">
                    <Link to="/services" className="block px-4 py-2 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 transition-colors font-semibold border-b border-slate-200">
                      {isRTL ? 'جميع الخدمات' : 'All Services'}
                    </Link>
                    {services.map((service, idx) => (
                      <Link 
                        key={service.id}
                        to={`/services/${serviceSlugMap[idx]}`}
                        className="block px-4 py-2 text-slate-700 hover:bg-cyan-50 hover:text-cyan-600 transition-colors text-sm"
                      >
                        {service.title}
                      </Link>
                    ))}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/events" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.events}
              </Link>
              <Link to="/cmes" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.cme}
              </Link>
              <Link to="/clients" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.clients}
              </Link>
              <Link to="/contact" className="text-slate-700 hover:text-cyan-500 font-medium transition-colors">
                {t.nav.contact}
              </Link>
              
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors font-medium"
              >
                <Globe size={18} />
                {language === 'en' ? 'العربية' : 'English'}
              </button>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
